import { useCallback, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type CardColumnConfig,
  type CardColumnId,
  type ColumnTone,
  DEFAULT_CARD_COLUMNS,
  DEFAULT_RESOLVED_RETENTION,
  DEFAULT_TASK_COLUMNS,
  HttpError,
  type ResolvedRetention,
  ResolvedRetentionSchema,
  type TaskColumnConfig,
  type TaskColumnId,
} from '@voll-intelligence/domain';

import { queryKeys } from '@web/core/query';
import { t } from '@web/i18n';
import {
  boardRepository,
  type BoardConfigResponse,
  type UpdateBoardConfigInput,
} from '@web/data/repositories';
import { useOrgContext, useSnackbar } from '@web/ui/core';
import { taskColumnTone } from '@web/ui/core/utils/board_tone';

export const LABEL_MAX = 40;

/**
 * Composite key for the label map. The stable id `resolved` exists on
 * both boards, so we namespace by board to keep their labels independent.
 */
type DraftKey = `card:${CardColumnId}` | `task:${TaskColumnId}`;

const cardKey = (id: CardColumnId): DraftKey => `card:${id}`;
const taskKey = (id: TaskColumnId): DraftKey => `task:${id}`;

type LabelMap = Partial<Record<DraftKey, string>>;

/**
 * Full editable config draft: per-board labels, per-card-column tones
 * and the org-level resolved-card retention. Task columns have no
 * tenant-configurable tone (their tone is derived from the stable id).
 */
interface ConfigDraft {
  labels: LabelMap;
  cardTones: Partial<Record<CardColumnId, ColumnTone>>;
  retention: ResolvedRetention;
}

export interface BoardColumnRow {
  readonly stableId: string;
  readonly defaultLabel: string;
  readonly displayedLabel: string;
  readonly tone: ColumnTone;
  readonly setLabel: (next: string) => void;
  /** Present only for card columns — task tones are not configurable. */
  readonly setTone?: (next: ColumnTone) => void;
}

function labelsToMap(
  cols: readonly { id: string; label: string }[],
  prefix: 'card' | 'task',
): LabelMap {
  const out: LabelMap = {};
  for (const c of cols) out[`${prefix}:${c.id}` as DraftKey] = c.label;
  return out;
}

function countPendingChanges(draft: ConfigDraft, saved: ConfigDraft): number {
  let n = 0;
  for (const c of DEFAULT_CARD_COLUMNS) {
    const key = cardKey(c.id);
    const labelChanged = (draft.labels[key] ?? c.label) !== (saved.labels[key] ?? c.label);
    const toneChanged = (draft.cardTones[c.id] ?? c.tone) !== (saved.cardTones[c.id] ?? c.tone);
    if (labelChanged || toneChanged) n += 1;
  }
  for (const c of DEFAULT_TASK_COLUMNS) {
    const key = taskKey(c.id);
    if ((draft.labels[key] ?? c.label) !== (saved.labels[key] ?? c.label)) n += 1;
  }
  if (draft.retention !== saved.retention) n += 1;
  return n;
}

function fromServer(response: BoardConfigResponse): ConfigDraft {
  const cardTones: Partial<Record<CardColumnId, ColumnTone>> = {};
  for (const c of response.cardColumns) cardTones[c.id] = c.tone;
  return {
    labels: {
      ...labelsToMap(response.cardColumns, 'card'),
      ...labelsToMap(response.taskColumns, 'task'),
    },
    cardTones,
    retention: response.resolvedRetention,
  };
}

const errorMessage = (err: unknown): string => {
  if (err instanceof HttpError) return err.message;
  if (err instanceof Error) return err.message;
  return t('orgBoardConfig.toast.error');
};

export function useOrgBoardConfigPage() {
  const { orgId } = useOrgContext();
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  const { data: server } = useQuery({
    queryKey: queryKeys.orgBoardConfig(orgId),
    queryFn: () => boardRepository.loadBoardConfig(orgId),
  });

  const [draft, setDraft] = useState<ConfigDraft | null>(null);
  const [saved, setSaved] = useState<ConfigDraft | null>(null);

  // Initial hydration from the server query. The mutation's onSuccess
  // handles all subsequent syncs explicitly — react-query invalidations
  // refresh the cached server value, but we leave the local draft alone
  // so user edits never get clobbered mid-flight.
  if (server && saved === null) {
    const next = fromServer(server);
    setSaved(next);
    setDraft(next);
  }

  const mutation = useMutation({
    mutationFn: (payload: UpdateBoardConfigInput) =>
      boardRepository.updateBoardConfig(orgId, payload),
    onSuccess: async (updated) => {
      queryClient.setQueryData<BoardConfigResponse | undefined>(
        queryKeys.orgBoardConfig(orgId),
        updated,
      );
      const next = fromServer(updated);
      setSaved(next);
      setDraft(next);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.orgBoardConfig(orgId) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.boards.conversation(orgId) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.boards.tasks(orgId) }),
      ]);
      showSnackbar({ severity: 'success', message: t('orgBoardConfig.toast.saved') });
    },
    onError: (err) => {
      showSnackbar({ severity: 'error', message: errorMessage(err) });
    },
  });

  // Stable (they only touch `setDraft`), so the column memos below recompute on `draft` alone.
  const patch = useCallback((fn: (d: ConfigDraft) => ConfigDraft): void => {
    setDraft((d) => (d === null ? d : fn(d)));
  }, []);

  const setLabel = useCallback(
    (key: DraftKey, next: string): void => {
      patch((d) => ({ ...d, labels: { ...d.labels, [key]: next } }));
    },
    [patch],
  );

  const setCardTone = useCallback(
    (id: CardColumnId, next: ColumnTone): void => {
      patch((d) => ({ ...d, cardTones: { ...d.cardTones, [id]: next } }));
    },
    [patch],
  );

  function setRetention(next: ResolvedRetention): void {
    patch((d) => ({ ...d, retention: next }));
  }

  const cardCols = useMemo<BoardColumnRow[]>(
    () =>
      DEFAULT_CARD_COLUMNS.map((c): BoardColumnRow => {
        const key = cardKey(c.id);
        return {
          stableId: c.id,
          defaultLabel: c.label,
          displayedLabel: draft?.labels[key] ?? c.label,
          tone: draft?.cardTones[c.id] ?? c.tone,
          setLabel: (next) => {
            setLabel(key, next);
          },
          setTone: (next) => {
            setCardTone(c.id, next);
          },
        };
      }),
    [draft, setLabel, setCardTone],
  );

  const taskCols = useMemo<BoardColumnRow[]>(
    () =>
      DEFAULT_TASK_COLUMNS.map((c): BoardColumnRow => {
        const key = taskKey(c.id);
        return {
          stableId: c.id,
          defaultLabel: c.label,
          displayedLabel: draft?.labels[key] ?? c.label,
          tone: taskColumnTone(c.id),
          setLabel: (next) => {
            setLabel(key, next);
          },
        };
      }),
    [draft, setLabel],
  );

  const pendingCount = useMemo(
    () => (draft && saved ? countPendingChanges(draft, saved) : 0),
    [draft, saved],
  );

  const isDirty = pendingCount > 0;

  function discard(): void {
    setDraft(saved);
  }

  function save(): void {
    if (!server || !draft) return;
    const cardColumns: CardColumnConfig[] = DEFAULT_CARD_COLUMNS.map((c) => ({
      id: c.id,
      label: draft.labels[cardKey(c.id)] ?? c.label,
      tone: draft.cardTones[c.id] ?? c.tone,
    }));
    const taskColumns: TaskColumnConfig[] = DEFAULT_TASK_COLUMNS.map((c) => ({
      id: c.id,
      label: draft.labels[taskKey(c.id)] ?? c.label,
    }));
    mutation.mutate({
      expectedV: server._v,
      cardColumns,
      taskColumns,
      resolvedRetention: draft.retention,
    });
  }

  return {
    isReady: server !== undefined,
    cardCols,
    taskCols,
    retention: draft?.retention ?? DEFAULT_RESOLVED_RETENTION,
    retentionOptions: ResolvedRetentionSchema.options,
    setRetention,
    isDirty,
    isSaving: mutation.isPending,
    discard,
    save,
  };
}
